import {
  ArrowTrendingUpIcon,
  BeakerIcon,
  BookmarkSquareIcon,
  BookOpenIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  CodeBracketIcon,
  CodeBracketSquareIcon,
  CommandLineIcon,
  CpuChipIcon,
  CubeTransparentIcon,
  DocumentTextIcon,
  LifebuoyIcon,
  QrCodeIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  SquaresPlusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import S3Logo from "/aws-s3-logo.svg";
import AzureLogo from "/azure-logo.png";
import BaseSpaceLogo from "/basespace-logo.png";
import BenchlingLogo from "/benchling-logo.png";
import BitbucketLogo from "/bitbucket-logo.png";
import BoxLogo from "/box-logo.svg";
import DatabricksLogo from "/databricks-logo.png";
import DataikuLogo from "/dataiku-logo.png";
import DockerLogo from "/docker-logo.png";
import DriveLogo from "/drive-logo.svg";
import DropboxLogo from "/dropbox-logo.svg";
import GCSLogo from "/gcs-logo.svg";
import GeminiLogo from "/gemini-logo.png";
import GeoLogo from "/geo-logo.png";
import GithubLogo from "/github-logo.svg";
import GitlabLogo from "/gitlab-logo.svg";
import GlobusLogo from "/globus-logo.png";
import NanoporeLogo from "/nanopore-logo.png";
import OpenAILogo from "/openai-logo.png";
import ClaudeLogo from "/claude-logo.svg";
import PacBioLogo from "/pacbio-logo.png";
import RedHatLogo from "/redhat-logo.png";
import SnowflakeLogo from "/snowflake-logo.png";
import SSHLogo from "/ssh-logo.png";
import ClusterServerLogo from "/cluster-server-logo.svg";
import TamarindLogo from "/tamarind-logo.svg";

import {
  type IDataSources,
  type SelectOption,
} from "@via/utils/interfaces.tsx";
import Config, { getDocsUrl } from "@via/utils/config.tsx";
import { LoginType, OnboardingStep, RevisionType, UserRole } from "common";
import GoogleLogo from "/google-logo.svg";
import type { McpIdeType } from "@via/pages/account/profile/personal-access-tokens/utils/code-generators.ts";

export const ROUTES = {
  HOME: "/activity",
  LOGIN: "/login",
  LOGIN_MFA: "/login/mfa",
  SIGNUP: "/signup",
  SELF_SIGNUP: "/self-signup",
  JOIN: "/join",
  GUEST_SIGNIN: "/guest-signin",
  GUEST_SIGNUP: "/guest-signup",
  ADMIN: "/admin",
  FEATURE_FLAGS: "/admin/feature-flags",
  PROFILE: "/profile",
  PROFILE_SECURITY: "/profile/security",
  CREDENTIALS: "/profile/credentials",
  RUN_ENVIRONMENTS: "/profile/run-environments",
  INTEGRATION_TOKENS: "/profile/integration-tokens",
  PERSONAL_ACCESS_TOKENS: "/profile/personal-access-tokens",
  WORKSPACES: "/workspaces",
  DATA: "/data",
  CREATE_DATA_SOURCE: "/data/create-data-source",
  DATA_SOURCE_PROVIDER_AMAZON: "/data/cloud-data-sources/amazon-s3",
  DATA_SOURCE_PROVIDER_GOOGLE: "/data/cloud-data-sources/google-cloud-storage",
  DATA_SOURCE_PROVIDER_SSH: "/data/ssh/ssh-data",
  DATA_SOURCE_PROVIDER_BASESPACE: "/data/token-based-data-sources/basespace",
  DATA_SOURCE_PROVIDER_GEO: "/data/public-data-sources/geo",
  DATA_SOURCE_PROVIDER_URL: "/data/public-data-sources/url",
  DATA_SOURCE_PROVIDER_REMOTE_STORAGE: "/data/remote-storage",
  COLLECTIONS: "/data/collections",
  PREMIUM_COLLECTIONS: "/data/collections",
  PREMIUM_DATASET_FILE: "/data/collections/premium/:datasetId",
  PROJECTS: "/projects",
  PIPELINES: "/pipelines",
  PIPELINE_PROCESSES: "/pipelines/processes",
  RUNS: "/runs",
  RUN_SCHEDULERS: "/runs/schedulers",
  RUN_ANALYTICS: "/runs/analytics",
  CATALOG: "/catalog",
  ACTIVITY: "/activity",
  CONTACT_SALES: "/contact-sales",
  APPS: "/apps",
  NOT_FOUND: "/404",
  WORKBENCH: "/workbench",
  WORKBENCH_MANAGE_STATUS: "/workbench/manage-status",
  AVAILABLE_WOKBENCH: "/workbench/available-workbench",
  INACTIVE_ORGANIZATION: "/inactive-organization",
  VMETA_DATASETS: "/data/collections",
  SAMPLE_TRACKERS: "/sample-trackers",
  SAMPLE_TRACKERS_DETAIL: "/sample-trackers/:projectId",
  SCAN: "/scan",
  ALIAS: "/a/*",
  PROJECT_SETTINGS: "/projects/:projectId/settings",
  PROJECT_PIPELINES: "/projects/:projectId/pipelines",
  PROJECT_RUNS: "/projects/:projectId/runs",
  PROJECT_DASHBOARD: "/projects/:projectId/dashboard",
  PROJECT_METADATA: "/projects/:projectId/metadata",
  RUN_DETAILS: "/runs/:runId",
  PIPELINE_WORKFLOW: "/pipelines/:pipelineId/workflow",
  PIPELINE_SETTINGS: "/pipelines/:pipelineId/settings",
  PIPELINE_DESCRIPTION: "/pipelines/:pipelineId/description",
  PIPELINE_ADVANCED: "/pipelines/:pipelineId/advanced",
  AVAILABLE_APPS: "/apps/available-apps",
  APP_MANAGE_STATUS: "/apps/manage-status",
  ORGANIZATION_ACTIVITY: "/organization/:organizationId/activity",
  ORGANIZATION_USERS: "/organization/:organizationId/users",
  ORGANIZATION_INVOICES: "/organization/:organizationId/invoices",
  ORGANIZATION_RUN_ENVIRONMENTS:
    "/organization/:organizationId/run-environments",
  ORGANIZATION_SETTINGS: "/organization/:organizationId/settings",
  ORGANIZATION_USAGE: "/organization/:organizationId/usage",
  CORTEX: "/cortex",
  CORTEX_MODEL_GARDEN: "/cortex/model-garden",
  CORTEX_MODEL_SKILL: "/cortex/model-skills",
};

export enum KEYS {
  PROJECTS = "projects",
  COLLECTIONS = "collections",
  WORKSPACES = "workspaces",
  PIPELINES = "pipelines",
  APIS = "apis",
  CLOUD_STORAGE = "cloud-storage",
  APPS = "apps",
  DATA = "data",
  RUNS = "runs",
  SAMPLE_TRACKERS = "sample-trackers",
}

export enum CREDENTIAL_TYPES {
  AMAZON = "amazon",
  GOOGLE = "google",
  SSH = "ssh",
}

export const SIGNUP_CTA = [
  {
    message:
      "Foundry is the world's most composable omics platform that lets you go faster",
    details: "Built by scientists, for scientists",
  },
  {
    message: "Unified metadata, pipelines and analytics",
    details: "The Biological Operating System for a multi-omics world",
  },
  {
    message:
      "Future proof your science while ensuring usability, reliability and reproducibility",
    details: "Duplicate and share any run, every parameter and every result",
  },
  {
    message:
      "Complexities of cloud orchestration eliminated, and compute costs optimized",
    details: "Focus on science not compute complexities and associated costs",
  },
];

export const PERMISSION = "permission";

export enum USER_ROLES {
  ADMIN = "admin",
  USER = "user",
  GUEST = "guest",
}

export enum PIPELINE_TYPES {
  NFCORE = "NF-Core",
  NEXTFLOW = "Nextflow",
  VIA_FOUNDRY = "ViaFoundry",
}

export enum OVERVIEW_FIELDS {
  NAME = "name",
  DESCRIPTION = "description",
  REVISION_COMMENT = "revisionComment",
}

export enum NODE_TYPES {
  InputNode = "inputNode",
  OutputNode = "outputNode",
  ModuleNode = "moduleNode",
  ProcessNode = "processNode",
  ModelNode = "modelNode",
  SkillNode = "skillNode",
}

export enum EDGE_TYPE_REGEX {
  InputNodeToInputProcess = "o-inPro-\\d+-\\d+-\\d+_i-\\d+-\\d+-\\d+-\\d+",
  OutputNodeToOutputProcess = "i-outPro-\\d+-\\d+-\\d+_o-\\d+-\\d+-\\d+-\\d+",
  InputNodeToInputModule = "o-inPro-\\d+-\\d+-\\d+_pi-\\d+-\\d+-\\d+-\\d+",
  OutputNodeToOutputModule = "i-outPro-\\d+-\\d+-\\d+_po-\\d+-\\d+-\\d+-\\d+",
  InputProcessToOutputProcess = "i-\\d+-\\d+-\\d+-\\d+_o-\\d+-\\d+-\\d+-\\d+",
  OutputProcessToInputProcess = "o-\\d+-\\d+-\\d+-\\d+_i-\\d+-\\d+-\\d+-\\d+",
  InputProcessToOutputModule = "i-\\d+-\\d+-\\d+-\\d+_po-\\d+-\\d+-\\d+-\\d+",
  OutputProcessToInputModule = "o-\\d+-\\d+-\\d+-\\d+_pi-\\d+-\\d+-\\d+-\\d+",
  OutputModuleToInputProcess = "po-\\d+-\\d+-\\d+-\\d+_i-\\d+-\\d+-\\d+-\\d+",
  OutputModuleToInputModule = "po-\\d+-\\d+-\\d+-\\d+_pi-\\d+-\\d+-\\d+-\\d+",
  InputModuleToOutputProcess = "pi-\\d+-\\d+-\\d+-\\d+_o-\\d+-\\d+-\\d+-\\d+",
  InputModuleToOutputModule = "pi-\\d+-\\d+-\\d+-\\d+_po-\\d+-\\d+-\\d+-\\d+",
}

export enum EDGE_TYPES {
  InputNodeToInputProcess = "InputNodeToInputProcess",
  OutputNodeToOutputProcess = "OutputNodeToOutputProcess",
  InputProcessToOutputProcess = "InputProcessToOutputProcess",
  OutputProcessToInputProcess = "OutputProcessToInputProcess",
  InputNodeToInputModule = "InputNodeToInputModule",
  OutputNodeToOutputModule = "OutputNodeToOutputModule",
  InputProcessToOutputModule = "InputProcessToOutputModule",
  OutputModuleToInputProcess = "OutputModuleToInputProcess",
  InputModuleToOutputProcess = "InputModuleToOutputProcess",
  OutputProcessToInputModule = "OutputProcessToInputModule",
  InputModuleToOutputModule = "InputModuleToOutputModule",
  OutputModuleToInputModule = "OutputModuleToInputModule",
}

export const PROTECTED = [
  KEYS.WORKSPACES,
  KEYS.PIPELINES,
  KEYS.APIS,
  KEYS.CLOUD_STORAGE,
  KEYS.APPS,
  KEYS.COLLECTIONS,
  KEYS.DATA,
  KEYS.RUNS,
  KEYS.SAMPLE_TRACKERS,
];

export const PAGE_NAMES = {
  HOME: "Home",
  ADMIN: "Admin Panel",
  PROFILE: "Profile",
  FEATURE_FLAGS: "Feature Flags",
  WORKSPACES: "Workspaces",
  DATA: "Data",
  DATASETS: "Datasets",
  PROJECTS: "Projects",
  PIPELINES: "Pipelines",
  CATALOG: "Catalog",
  ACTIVITY: "Activity",
  APPS: "Apps",
  RUNS: "Runs",
  SAMPLE_TRACKERS: "Sample Trackers",
  SCAN: "Scan",
  USER_PROFILE: "User Profile",
  WORKBENCH: "Workbench",
  CORTEX: "Cortex",
};

export const TABLE_TYPES = {
  PROJECTS: "projectsTable",
};

export const BUTTONS = {
  NEW_WORKSPACE: "Create Workspace",
  NEW_PROJECT: "Create Project",
  CREATE_APP: "Create App",
  CREATE_PIPELINE: "Create Pipeline",
  ANALYZE_DATA: "Analyze Data",
  RUN: "Run",
  CREATE_ENVIRONMENT: "Create Environment",
  CREATE_CREDENTIAL: "Create Credential",
  CREATE_PUBLIC_ENVIRONMENT: "Create Public Environment",
  NEW_METADATA_FIELD: "New Field",
  ADD_PIPELINE: "Add Pipeline",
  CREATE_GROUP: "Create Group",
  CREATE_TOKEN: "Create Token",
  CREATE_INSTRUCTION: "Create Instruction",
  IMPORT_FROM_SPREADSHEET: "Add via Spreadsheet",
  ADD_NEW_FILE: "Browse & Add Files",
  ADD_FOLDER_PATH: "Add Folder for Download Only",
  CREATE_WORKBENCH: "Create Workbench",
  CREATE_PROJECT_BUCKET: "Create Project Bucket",
  CREATE_USER: "Create User",
  EXPORT_DATA: "Export Data",
  CREATE_DATASET: "Create Dataset",
  CREATE_DATA_SOURCE: "Create Data Source",
  ADD_CUSTOMER_BOUNDARY: "Add Customer Boundary",
  NEW_ORGANIZATION: "New Organization",
  NEW_METADATA_COLLECTION: "New Collection",
  NEW_API_CONFIG: "New API Config",
  NEW_SAMPLE_TRACKER: "Create Sample Tracker",
  REGISTER_MODEL: "Register Model",
  MANAGE_DATASET: "Manage Dataset",
  LINK_EXISTING_FILES: "Link Existing Files",
  ADD_FIELDS: "Add Fields",
};

export const AUTH_LINKS = {
  GOOGLE_SSO: "/api/auth/v1/oauth2/google-sign-in",
  GITHUB_SSO: "/api/auth/v1/oauth2/github-sign-in",
  SAML_SSO: "/api/auth/v1/saml/sign-in",
};

export const NAVIGATION = [
  {
    name: PAGE_NAMES.DATA,
    href: ROUTES.DATA,
    icon: CircleStackIcon,
  },
  {
    name: PAGE_NAMES.SAMPLE_TRACKERS,
    href: ROUTES.SAMPLE_TRACKERS,
    icon: ClipboardDocumentCheckIcon,
  },
  {
    name: PAGE_NAMES.PIPELINES,
    href: ROUTES.PIPELINES,
    icon: CubeTransparentIcon,
  },
  {
    name: PAGE_NAMES.PROJECTS,
    href: ROUTES.PROJECTS,
    icon: BeakerIcon,
  },
  {
    name: PAGE_NAMES.RUNS,
    href: ROUTES.RUNS,
    icon: RocketLaunchIcon,
  },
  {
    name: PAGE_NAMES.APPS,
    href: ROUTES.APPS,
    icon: CodeBracketSquareIcon,
  },
];

/** Shown only while the installation turns AGENT_WORKSPACE_JOBS_ENABLED on (agent-7-4, agent-d73). */
export const AGENT_WORKSPACES_NAVIGATION = {
  name: PAGE_NAMES.WORKSPACES,
  href: ROUTES.WORKSPACES,
  icon: ServerStackIcon,
};

export const WORKBENCH_NAVIGATION = {
  name: PAGE_NAMES.WORKBENCH,
  href: ROUTES.WORKBENCH,
  icon: CodeBracketIcon,
};

export const CORTEX_NAVIGATION = {
  name: PAGE_NAMES.CORTEX,
  href: ROUTES.CORTEX,
  icon: LifebuoyIcon,
};

export const SCAN_NAVIGATION = {
  name: PAGE_NAMES.SCAN,
  href: ROUTES.SCAN,
  icon: QrCodeIcon,
};

export const SAMPLE_TRACKERS_NAVIGATION = {
  name: "Sample Trackers",
  href: "/sample-trackers",
  icon: DocumentTextIcon,
};

export const ACTIVITY_NAVIGATION = {
  name: PAGE_NAMES.ACTIVITY,
  href: ROUTES.ACTIVITY,
  icon: ArrowTrendingUpIcon,
};

export const ACTIVITY_CARDS = [
  {
    title: "Access Data",
    href: "/data/show-all",
    details:
      "Create and manage connections, browse data and set the stage for pipeline runs",
    icon: CircleStackIcon,
  },
  {
    title: "Create a Project",
    href: "/projects",
    details: "Create a project and develop piplines in a governed environment",
    icon: CubeTransparentIcon,
  },
  {
    title: "Integrate AI",
    href: "/catalog",
    details: "Register external models into Foundry for use in Pipelines",
    icon: CpuChipIcon,
  },
  {
    title: "Build & Launch Apps",
    href: "/apps",
    details: "Use pre-built apps or develop your own to interrogate results",
    icon: CommandLineIcon,
  },
];

export const CREATE_DROPDOWN = [
  {
    name: "New Dataset",
    href: "/data/collections?new=true",
    icon: CircleStackIcon,
  },
  {
    name: "New Project",
    href: "/projects/?new=true",
    icon: BeakerIcon,
  },
  {
    name: "New Pipeline",
    href: "/pipelines/?new=true",
    icon: CubeTransparentIcon,
  },
  {
    name: "New App",
    href: "/apps/available-apps/?new=true",
    icon: CodeBracketSquareIcon,
  },
];

export const WORKBENCH_CREATE_DROPDOWN = {
  name: "New Workbench",
  href: "/workbench/available-workbench/?new=true",
  icon: CodeBracketIcon,
};

export const CATALOG_FILTERS = [
  {
    name: "Datasets",
    icon: CircleStackIcon,
    color: "stroke-[#fdb33c]",
    href: "#",
    current: true,
  },
  {
    name: "Models",
    icon: CpuChipIcon,
    color: "stroke-[#8417de]",
    href: "#",
    current: false,
  },
  {
    name: "Pipelines",
    icon: CubeTransparentIcon,
    color: "stroke-[#409537]",
    href: "#",
    current: false,
  },
  {
    name: "Apps",
    icon: CodeBracketSquareIcon,
    color: "stroke-[#fe7f65]",
    href: "#",
    current: false,
  },
];

export enum EmptyWorkspaceCardTitle {
  ViewSandbox = "View Sandbox",
  AddResources = "Add Resources",
  InviteUsers = "Invite Users",
}

export const EMPTY_WORKSPACE_CARDS = [
  {
    title: EmptyWorkspaceCardTitle.ViewSandbox,
    href: "#",
    details: "Start with a demo Workspace that has sample assets",
    icon: BeakerIcon,
  },
  {
    title: EmptyWorkspaceCardTitle.AddResources,
    href: "#",
    details: "Publish relevant content across all of your projects",
    icon: SquaresPlusIcon,
  },
  {
    title: EmptyWorkspaceCardTitle.InviteUsers,
    href: "#",
    details: "Add collaborators from your organization",
    icon: UserPlusIcon,
  },
];

export enum EmptyMetadataCardTitle {
  CreateMetadata = "Create a Metadata",
}

export const EMPTY_METADATA_CARDS = [
  {
    title: EmptyMetadataCardTitle.CreateMetadata,
    href: "#",
    details: "Add metadata to your project to better organize and search",
    icon: DocumentTextIcon,
  },
];

// Nested routes need to be relative, which means no "/" in front (not absolute)
export const PROJECTS_TABS = [
  { name: "Dashboard", href: "dashboard" },
  { name: "Portal", href: "portal" },
  { name: "Metadata", href: "metadata" },
  { name: "Pipelines", href: "pipelines" },
  { name: "Runs", href: "runs" },
  { name: "Settings", href: "settings" },
];

export const RUNS_TABS = {
  RUNS: "Runs",
  SCHEDULERS: "Schedulers",
  ANALYTICS: "Run Analytics",
};

export const RUN_FILE_INPUT_TABS = [
  { name: "Files", href: "files" },
  { name: "Manually", href: "manually" },
];

export const INPUT_FILE_MODAL_TABS = {
  DATASET: "Dataset",
  MANUAL: "Manually / Single File",
};

export const PROFILE_TABS = [
  { name: "Run Environments", href: "run-environments" },
  { name: "Credentials", href: "credentials" },
  {
    name: "Integration Tokens",
    href: "integration-tokens",
  },
  {
    name: "Personal Access Tokens",
    href: "personal-access-tokens",
  },
  {
    name: "Connect AI Assistants",
    href: "connect-claude",
  },
  { name: "User Groups", href: "user-groups" },
  { name: "Preferences", href: "preferences" },
  {
    name: "Change Password",
    href: "change-password",
  },
  { name: "Security", href: "security" },
  {
    name: "Deactivate Account",
    href: "deactivate-account",
  },
];

export const ADMIN_PROFILE_TABS = [
  { name: "Users", href: "users" },
  { name: "Feature Flags", href: "feature-flags" },
  { name: "Customer Boundary", href: "customer-boundary" },
  { name: "Organizations", href: "organizations" },
  { name: "Organization Charge History", href: "organization-charge-history" },
  { name: "Notification Settings", href: "notification-settings" },
  { name: "Access Codes", href: "access-codes" },
  { name: "Tags", href: "tags" },
  { name: "Trusted Repositories", href: "trusted-repositories" },
  { name: "Import Sign Ins", href: "import-logins" },
  { name: "Health Check", href: "health-check" },
];

export const EDIT_PROCESS_TABS = [
  { name: "Overview", href: "#", isDisplayed: true },
  { name: "Parameters", href: "#", isDisplayed: true },
  { name: "Scripts", href: "#", isDisplayed: true },
  { name: "Files", href: "#", isDisplayed: true },
  { name: "Revisions", href: "#", isDisplayed: true },
  { name: "Example", href: "#", isDisplayed: true },
  { name: "Provenance", href: "#", isDisplayed: true },
  { name: "Model", href: "#", isDisplayed: false },
];

export const APPS_TABS = [
  { name: "Available Apps", href: "available-apps" },
  { name: "Manage Status", href: "manage-status" },
];

export const WORKBENCH_TABS = [
  { name: "Available Workbench", href: "available-workbench" },
  { name: "Manage Status", href: "manage-status" },
];

export const getCortexTabs = (showAdditionalTabs: boolean = false) => [
  { name: "Model Garden", href: "model-garden" },
  ...(showAdditionalTabs
    ? [{ name: "Foundry Models", href: "foundry-models" }]
    : []),
  { name: "Model Skills", href: "model-skills" },
  ...(showAdditionalTabs
    ? [
        { name: "MCP Servers", href: "mcp-servers" },
        { name: "Knowledge Builder", href: "knowledge-builder" },
      ]
    : []),
];

export const PIPELINE_TABS = [
  { name: "Description", href: "description" },
  { name: "Workflow", href: "workflow" },
  { name: "Advanced", href: "advanced" },
  { name: "Settings", href: "settings" },
  { name: "Git", href: "git" },
  { name: "Provenance", href: "provenance" },
];

export const NOT_FOUND_LINKS = [
  {
    name: "Documentation",
    href: getDocsUrl(),
    description: "Comprehensive product documentation",
    icon: BookOpenIcon,
  },
  {
    name: "API Reference",
    href: `${Config.HOST}/api-docs`,
    description: "A complete API reference for Foundry",
    icon: CommandLineIcon,
  },
  {
    name: "Training & Guides",
    href: getDocsUrl("/get-started/"),
    description: "Tutorials and training for popular workflows",
    icon: BookmarkSquareIcon,
  },
];

// Groovy uses Python for now because we need to roll a custom plugin for supporting it
export enum LANGUAGE_MODES {
  SHELL = "shell",
  GROOVY = "groovy",
  PERL = "perl",
  PYTHON = "python",
  R = "r",
  DOCKER = "dockerfile",
  MARKDOWN = "markdown",
  YAML = "yaml",
}

export const LANGUAGE_OPTIONS = [
  { label: "Shell", value: "shell" },
  { label: "Groovy", value: "groovy" },
  { label: "Perl", value: "perl" },
  { label: "Python", value: "python" },
  { label: "R", value: "r" },
];

export const EDIT_PROCESS_ADVANCED_FILES = [
  { label: "Script", filename: "body" },
  { label: "Header Script", filename: "header" },
  { label: "Footer Script", filename: "footer" },
];

export const ACCOUNT = [
  {
    name: "Profile",
    href: ROUTES.PROFILE,
    requiredRole: USER_ROLES.USER,
  },
  {
    name: "Admin Panel",
    href: ROUTES.ADMIN,
    requiredRole: USER_ROLES.ADMIN,
  },
];

export const ERROR =
  "An error has occurred while retrieving your request. Try refreshing the page.";

export const ERROR_YOU_ARE_NOT_AUTHORIZED_TO_ACCESS_THIS_PIPELINE =
  "You are not authorized to access this pipeline";

export const PROJECTS_TABLE = [{ name: "Delete Project", href: "#" }];

export const RUN_ENVIRONMENTS_TABLE = [
  { name: "Edit", href: "#" },
  { name: "Remove", href: "#" },
];

export const CREDENTIAL_KEYS_TABLE = [
  { name: "View", href: "#" },
  { name: "Edit", href: "#" },
  { name: "Remove", href: "#" },
];

export const DATA_COLLECTIONS_TABLE = [
  { name: "View", href: "#" },
  { name: "Rename", href: "#" },
  // { name: "Publish", href: "#" },
  { name: "Delete", href: "#" },
];

export const RUN_INPUT_COLLECTION_TABLE_OPTIONS = [{ name: "View", href: "#" }];

export const RUN_TABLE_OPTIONS = [
  { name: "View Run", href: "#" },
  { name: "Delete Run", href: "#" },
];

export const MY_APPS_TABLE_OPTIONS = [
  { name: "Launch", href: "#" },
  { name: "Edit", href: "#" },
  { name: "Remove", href: "#" },
];

export const APP_STATUS_TABLE_OPTIONS = {
  LAUNCH: { name: "Launch", href: "#" },
  OPEN: { name: "Open", href: "#" },
  TERMINATE: { name: "Terminate", href: "#" },
};

export const WORKSPACE_LIST_TABLE_OPTIONS = [
  { name: "Add to Project", href: "#" },
  { name: "Remove", href: "#" },
  { name: "Share", href: "#" },
];

export const WORKSPACE_ASSETS_TABLE_OPTIONS = [
  { name: "Edit", href: "#" },
  { name: "Remove", href: "#" },
  { name: "Publish to Catalog", href: "#" },
];

export const PIPELINE_TABLE_OPTIONS = [
  { name: "View Pipeline", href: "#" },
  { name: "Run", href: "#" },
  { name: "Remove", href: "#" },
];

export const USER_GROUPS_TABLE = [
  { name: "Edit Name", href: "#", id: "userGroupsTableEdit" },
  { name: "Delete", href: "#", id: "userGroupsTableDelete" },
  { name: "Edit/View Group", href: "#", id: "userGroupsTableView" },
];

export const USER_GROUP_MEMBERS_TABLE = [{ name: "Delete", href: "#" }];

export const PROJECT_BUCKET_MEMBERS_TABLE = [{ name: "Delete", href: "#" }];

// @@TODO Clean up messaging here
export const EMPTY_TABLE_MESSAGE = {
  DEFAULT: "No data to display",
  RUN_FILE_INPUTS: "You don't have any data collections.",
  PROJECTS:
    "It doesn't look like you have any projects. Create a project to get started.",
  PIPELINES:
    "It doesn't look like you have any Pipelines. Create a Pipeline to see them populate.",
  RUNS: "It doesn't look like you have any run history. Create a Pipeline to conduct runs, which will show up on this page",
  RUN_SCHEDULERS:
    "It doesn't look like you have any run schedulers. Create a scheduler to get started.",
  WORKSPACES:
    "It doesn't look like you have any workspaces. Create a workspace to get started.",
  WORKSPACE_ASSETS:
    "It doesn't look like you have any workspace assets. Create a workspace assets to get started.",
  APPS: "It doesn't look like you have any Apps right now.",
  APP_HISTORY: `It doesn't look like you have any App history. Launch apps from the "My Apps" tab.`,
  PROCESSES: "There are no processes associated with this pipeline.",
  METADATA_COLLECTION_FIELDS:
    "There are no fields associated with this collection.",
  REPORTS: "No reports were available for this run.",
  DOWNLOAD_REPORTS: "No downloadable reports were found for this run.",
  LOGS: "No logs were available for this run.",
  DATASETS:
    "It doesn't look like you have any datasets. Create a datasets to get started.",
  WORKBENCH:
    "It doesn't look like you have any workbench. Create a workbench to get started.",
  WORKBENCH_INSTANCE_LIST:
    "It doesn't look like you have any workbench instances. Launch workbench from the 'Available Workbench' tab.",
  USER_LIST: "No User to Display",
  DEPLOYMENT_CONFIGS: "No deployment configurations available for this model.",
  RUN_ATTEMPTS: "No run attempts found for this run.",
  API_CONFIGS: "No API configs available",
  IMPORT_CONFIGS: "No data imports yet",
};

export const DATA_SOURCES: IDataSources[] = [
  {
    id: "Amazon",
    name: "S3 Bucket",
    componentName: "AmazonS3",
    alt: "S3 Logo",
    category: "cloud-storage",
    icon: S3Logo,
    href: "amazon-s3",
    comingSoon: false,
  },
  {
    id: "Google",
    name: "GCS",
    componentName: "GoogleCloudStorage",
    alt: "GCS Logo",
    category: "cloud-storage",
    icon: GCSLogo,
    href: "google-cloud-storage",
    comingSoon: false,
  },
  {
    id: "Ssh",
    name: "Cluster / Server",
    componentName: "SshData",
    alt: "Cluster or server logo",
    category: "ssh-data",
    icon: ClusterServerLogo,
    href: "ssh-data",
    comingSoon: false,
  },
  {
    id: "BaseSpace",
    name: "BaseSpace",
    description: "Illumina Basespace genomics platform",
    componentName: "",
    alt: "BaseSpace Logo",
    category: "token-based",
    icon: BaseSpaceLogo,
    href: "basespace",
    comingSoon: false,
  },
  {
    id: "Geo",
    name: "SRA / GEO",
    componentName: "GeoSource",
    alt: "Geo Logo",
    category: "apis",
    icon: GeoLogo,
    href: "geo-api",
    comingSoon: false,
  },
  {
    id: "Url",
    name: "URL",
    componentName: "UrlData",
    alt: "URL Logo",
    category: "apis",
    icon: "GlobeAltIcon",
    href: "url",
    comingSoon: false,
  },
  {
    id: "RemoteServer",
    name: "Remote server",
    description: "An SFTP, FTP or FTPS server you sign in to",
    componentName: "RemoteStorage",
    alt: "Remote server icon",
    category: "remote-storage",
    icon: "ServerStackIcon",
    href: "remote-storage",
    comingSoon: false,
  },
];

/** data-2-1: shown only when this installation registered the provider's OAuth app. */
export const OAUTH_DATA_SOURCES: IDataSources[] = [
  {
    id: "GoogleDrive",
    name: "Google Drive",
    description: "Your Google Drive, read only, after you sign in to Google",
    componentName: "RemoteStorageOAuth",
    alt: "Google Drive icon",
    category: "remote-storage-oauth",
    href: "remote-storage",
    comingSoon: false,
  },
  {
    id: "Dropbox",
    name: "Dropbox",
    description: "Your Dropbox, read only, after you sign in to Dropbox",
    componentName: "RemoteStorageOAuth",
    alt: "Dropbox icon",
    category: "remote-storage-oauth",
    href: "remote-storage",
    comingSoon: false,
  },
];

export const DATA_SOURCES2: IDataSources[] = [
  {
    id: "Amazon",
    name: "Amazon Web Services",
    description: "Connect to AWS S3 service",
    componentName: "AmazonS3",
    alt: "S3 Logo",
    category: "cloud-storage",
    icon: S3Logo,
    href: "amazon-s3",
    comingSoon: false,
  },
  {
    id: "Google",
    name: "Google Cloud Storage",
    description: "Access Google Cloud Platform Resources",
    componentName: "GoogleCloudStorage",
    alt: "GCS Logo",
    category: "cloud-storage",
    icon: GCSLogo,
    href: "google-cloud-storage",
    comingSoon: false,
  },
  {
    id: "Ssh",
    name: "Cluster / Server",
    description:
      "Files on a cluster or server you run on, over SSH or the remote agent",
    componentName: "SshData",
    alt: "Cluster or server logo",
    category: "ssh-data",
    icon: ClusterServerLogo,
    href: "ssh-data",
    comingSoon: false,
  },
  {
    id: "BaseSpace",
    name: "BaseSpace",
    description: "Illumina Basespace genomics platform",
    componentName: "",
    alt: "BaseSpace Logo",
    category: "token-based",
    icon: BaseSpaceLogo,
    href: "basespace",
    comingSoon: false,
  },
  {
    id: "Geo",
    name: "Geo",
    componentName: "GeoSource",
    alt: "Geo Logo",
    category: "apis",
    icon: GeoLogo,
    href: "geo-api",
    comingSoon: false,
  },
  {
    id: "Url",
    name: "Url",
    componentName: "GeoSource",
    alt: "Geo Logo",
    category: "apis",
    icon: GeoLogo,
    href: "geo-api",
    comingSoon: false,
  },
];

export const UPCOMING_DATA_SOURCES: IDataSources[] = [
  {
    id: "AzureBlob",
    name: "Azure Blob",
    componentName: "",
    alt: "Azure Blob Logo",
    category: "cloud-storage",
    icon: AzureLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "DataBricks",
    name: "Databricks",
    componentName: "",
    alt: "Databricks Logo",
    category: "cloud-storage",
    icon: DatabricksLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "SnowFlake",
    name: "Snowflake",
    componentName: "",
    alt: "Snowflake Logo",
    category: "cloud-storage",
    icon: SnowflakeLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Benchling",
    name: "Benchling",
    componentName: "",
    alt: "Benchling Logo",
    category: "apis",
    icon: BenchlingLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Box",
    name: "Box",
    componentName: "",
    alt: "Box Logo",
    category: "apis",
    icon: BoxLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Dropbox",
    name: "Dropbox",
    componentName: "",
    alt: "Dropbox Logo",
    category: "apis",
    icon: DropboxLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Drive",
    name: "Drive",
    componentName: "",
    alt: "Drive Logo",
    category: "apis",
    icon: DriveLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Github",
    name: "Github",
    componentName: "",
    alt: "Github Logo",
    category: "apis",
    icon: GithubLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "Globus",
    name: "Globus",
    componentName: "",
    alt: "Globus Logo",
    category: "apis",
    icon: GlobusLogo,
    href: "#",
    comingSoon: true,
  },
  {
    id: "BaseSpace",
    name: "BaseSpace",
    componentName: "",
    alt: "BaseSpace Logo",
    category: "apis",
    icon: BaseSpaceLogo,
    href: "#",
    comingSoon: true,
  },
];
export const INTEGRATIONS = [
  {
    name: "Databricks",
    alt: "Databricks Logo",
    icon: DatabricksLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Benchling",
    alt: "Benchling Logo",
    icon: BenchlingLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Github",
    alt: "Github Logo",
    icon: GithubLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Bitbucket",
    alt: "Bitbucket Logo",
    icon: BitbucketLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "PacBio",
    alt: "PacBio Logo",
    icon: PacBioLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Geo",
    alt: "Geo Logo",
    icon: GeoLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Nanopore",
    alt: "Nanopore Logo",
    icon: NanoporeLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Snowflake",
    alt: "Snowflake Logo",
    icon: SnowflakeLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Globus",
    alt: "Globus Logo",
    icon: GlobusLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "OpenAI",
    alt: "OpenAI Logo",
    icon: OpenAILogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Gemini",
    alt: "Gemini Logo",
    icon: GeminiLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Dataiku",
    alt: "Dataiku Logo",
    icon: DataikuLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "RedHat",
    alt: "RedHat Logo",
    icon: RedHatLogo,
    href: "#",
    comingSoon: true,
  },
  {
    name: "Docker Hub",
    alt: "Docker Logo",
    icon: DockerLogo,
    href: "#",
    comingSoon: true,
  },
];

export enum ParameterTypes {
  Inputs = "inputParameters",
  Outputs = "outputParameters",
}

export enum ButtonColor {
  Default = "default",
  Red = "red",
  Gray = "gray",
  Green = "green",
}

export enum EditProcessSections {
  Overview = "Overview",
  Parameters = "Parameters",
  Scripts = "Scripts",
  Files = "Files",
  Revisions = "Revisions",
  Example = "Example",
  Provenance = "Provenance",
  Model = "Model",
}

export enum BadgeStatus {
  DefaultFillColor = "fill-gray-400",
  ErrorFillColor = "fill-red-500",
  SuccessFillColor = "fill-green-500",
  TerminatedFillColor = "fill-gray-600",
  WaitingFillColor = "fill-amber-500",
  RunningFillColor = "fill-sky-500",
  DefaultTextColor = "text-gray-400",
  ErrorTextColor = "text-red-500",
  SuccessTextColor = "text-green-500",
  TerminatedTextColor = "text-gray-600",
  WaitingTextColor = "text-amber-500",
  RunningTextColor = "text-sky-500",
  DefaultText = "Not Submitted",
  ErrorText = "Error",
  ActiveText = "Active",
  CreatingText = "Creating",
  NotDeployedText = "Not deployed",
  SuccessText = "Completed",
  TerminatedText = "Terminated",
  RunningText = "Running",
  ConnectingText = "Connecting",
  WaitingText = "Waiting",
  InitializingText = "Initializing",
  PendingText = "Pending",
}

export enum ElementSize {
  Small = "small",
  Large = "large",
}

export enum ModalSize {
  Small = "small",
  Large = "large",
  XLarge = "xLarge",
}

export const MaximumBlobSize = 15700000;

export const FileTypes: SelectOption[] = [
  {
    label: "FASTQ",
    value: "fastq",
  },
  {
    label: "BAM",
    value: "bam",
  },
  {
    label: "BAI",
    value: "bai",
  },
  {
    label: "BED",
    value: "bed",
  },
  {
    label: "CSV",
    value: "csv",
  },
  {
    label: "TAB",
    value: "tab",
  },
  {
    label: "TSV",
    value: "tsv",
  },
  {
    label: "TXT",
    value: "txt",
  },
  {
    label: "VCF",
    value: "vcf",
  },
];

export const CollectionTypes: SelectOption[] = [
  {
    label: "Single List",
    value: "single",
  },
  {
    label: "Paired List",
    value: "pair",
  },
  {
    label: "Triple List",
    value: "triple",
  },
];

export enum EditorState {
  Editing = "Editing",
  Canceled = "Canceled",
  Saved = "Saved",
}

export enum AdvancedFilesActions {
  Delete = "Delete",
  Rename = "Rename",
  Upload = "Upload",
}

export enum AppsActions {
  Launch = "Launch",
  Redeploy = "Retry deploy",
  Edit = "Edit",
  Remove = "Remove",
  Terminate = "Terminate",
  Open = "Open",
}

export const AwsRegions: SelectOption[] = [
  { label: "US East (Ohio) | us-east-2", value: "us-east-2" },
  { label: "US East (N. Virginia) | us-east-1", value: "us-east-1" },
  { label: "US West (N. California) | us-west-1", value: "us-west-1" },
  { label: "US West (Oregon) | us-west-2", value: "us-west-2" },
  { label: "Africa (Cape Town) | af-south-1", value: "af-south-1" },
  { label: "Asia Pacific (Hong Kong) | ap-east-1", value: "ap-east-1" },
  { label: "Asia Pacific (Hyderabad) | ap-south-2", value: "ap-south-2" },
  { label: "Asia Pacific (Jakarta) | ap-southeast-3", value: "ap-southeast-3" },
  {
    label: "Asia Pacific (Melbourne) | ap-southeast-4",
    value: "ap-southeast-4",
  },
  { label: "Asia Pacific (Mumbai) | ap-south-1", value: "ap-south-1" },
  { label: "Asia Pacific (Osaka) | ap-northeast-3", value: "ap-northeast-3" },
  { label: "Asia Pacific (Seoul) | ap-northeast-2", value: "ap-northeast-2" },
  {
    label: "Asia Pacific (Singapore) | ap-southeast-1",
    value: "ap-southeast-1",
  },
  { label: "Asia Pacific (Sydney) | ap-southeast-2", value: "ap-southeast-2" },
  { label: "Asia Pacific (Tokyo) | ap-northeast-1", value: "ap-northeast-1" },
  { label: "Canada (Central) | ca-central-1", value: "ca-central-1" },
  { label: "Canada West (Calgary) | ca-west-1", value: "ca-west-1" },
  { label: "Europe (Frankfurt) | eu-central-1", value: "eu-central-1" },
  { label: "Europe (Ireland) | eu-west-1", value: "eu-west-1" },
  { label: "Europe (London) | eu-west-2", value: "eu-west-2" },
  { label: "Europe (Milan) | eu-south-1", value: "eu-south-1" },
  { label: "Europe (Paris) | eu-west-3", value: "eu-west-3" },
  { label: "Europe (Spain) | eu-south-2", value: "eu-south-2" },
  { label: "Europe (Stockholm) | eu-north-1", value: "eu-north-1" },
  { label: "Europe (Zurich) | eu-central-2", value: "eu-central-2" },
  { label: "Israel (Tel Aviv) | il-central-1", value: "il-central-1" },
  { label: "Middle East (Bahrain) | me-south-1", value: "me-south-1" },
  { label: "Middle East (UAE) | me-central-1", value: "me-central-1" },
  { label: "South America (São Paulo) | sa-east-1", value: "sa-east-1" },
  { label: "AWS GovCloud (US-East) | us-gov-east-1", value: "us-gov-east-1" },
  { label: "AWS GovCloud (US-West) | us-gov-west-1", value: "us-gov-west-1" },
];

export const LanguageOptions: SelectOption[] = [
  {
    label: "Groovy",
    value: "groovy",
  },
  {
    label: "Dockerfile",
    value: "dockerfile",
  },
  {
    label: "Markdown",
    value: "markdown",
  },
  {
    label: "Perl",
    value: "perl",
  },
  {
    label: "Python",
    value: "python",
  },
  {
    label: "R",
    value: "r",
  },
  {
    label: "Shell",
    value: "shell",
  },
  {
    label: "YAML",
    value: "yaml",
  },
];

export const TREE_FILE_MESSAGES = {
  DUPLICATE_FILES:
    "A file or folder with this name already exists in the directory",
  FILENAME_REQUIRED: "A file or folder name is required",
  INVALID_FILES: "File or Folder Name is Not Valid",
  UNABLE_TO_DELETE: "Unable to delete file",
  NEXTFLOW_REQUIRED: "This file is required",
};

export const DUPLICATE_FILES =
  "A file or folder with this name already exists in the directory";
export const FILENAME_REQUIRED_DETAILS = "A file or folder name is required";

export const INVALID_FILES_MESSAGE = "File or Folder Name is Not Valid";

export const INPUT_OUTPUT_PARAMS = [
  {
    name: "Input Parameter",
    nodeType: "inputNode",
    color: "text-amber-500",
  },
  {
    name: "Output Parameter",
    nodeType: "outputNode",
    color: "text-green-700",
  },
];

export const EDITOR_HEIGHT = "72dvh";

export const INITIATE_NEW_RUN_BUTTON = [{ name: "Start", href: "#" }];
export const INITIATE_RUN_BUTTON = [
  { name: "ReRun", href: "#" },
  { name: "Resume", href: "#" },
];
export const TERMINATE_RUN_BUTTON = [{ name: "Terminate", href: "#" }];

export const CredentialTypeOptions = [
  {
    label: "Amazon",
    value: "amazon",
    icon: S3Logo,
  },
  {
    label: "Google",
    value: "google",
    icon: GCSLogo,
  },
  {
    label: "SSH",
    value: "ssh",
    icon: SSHLogo,
  },
];

export const FILE_UPLOAD_DISCLAIMER =
  "Please note that if you lack the necessary permissions to upload data to the designated source, the upload will not be successful. If you encounter this issue, contact your system administrator to request access.";

export enum RunTabs {
  RunSettings = "Configurations",
  Advanced = "Advanced",
  Logs = "Logs",
  Reports = "Reports",
  Downloads = "Downloads",
  Test = "Test",
  Settings = "Settings",
}

export const MAX_UPLOAD_FILE_SIZE = 25 * 1024 * 1024 * 1024; // 25GB in bytes
export const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

/**
 * Enum for semantic version bump types.
 */
export const SemanticRevisionTypes = [
  {
    label: "Patch (1.0.0 ➔ 1.0.1): Backward-compatible bug fixes",
    value: RevisionType.Patch,
  },
  {
    label: "Minor (1.0.1 ➔ 1.1.0): Backward-compatible new features",
    value: RevisionType.Minor,
  },
  {
    label: "Major (1.1.0 ➔ 2.0.0): Incompatible changes",
    value: RevisionType.Major,
  },
];

export const IntegrationTokensLogo = {
  googleCloud: GoogleLogo,
  openAi: OpenAILogo,
  anthropic: ClaudeLogo,
  github: GithubLogo,
  gitlab: GitlabLogo,
  bitbucket: BitbucketLogo,
  baseSpace: BaseSpaceLogo,
  gemini: GeminiLogo,
  tamarind: TamarindLogo,
};

export const INTEGRATION_TOKEN_TABLE = [
  { name: "Delete", href: "#" },
  { name: "Edit", href: "#" },
];

export const CUSTOM_INSTRUCTION_TABLE = [
  { name: "Delete", href: "#" },
  { name: "Edit", href: "#" },
];

export const TEXT_FILES_EXTENSIONS =
  ".txt, .js, .ts, .py, .java, .sh, .bash, .pl, .config, .nf, .groovy, .md, .rmd, .yaml, .yml, .r";

export const RUN_MANAGEMENT_MODALS = {
  DUPLICATE_RUN: "Duplicate Run",
  CHANGE_PROJECT: "Change Project",
};

export const MY_WORKBENCH_TABLE_OPTIONS = [
  { name: "Launch", href: "#" },
  { name: "Edit", href: "#" },
  { name: "Remove", href: "#" },
];

export enum WorkbenchActions {
  Launch = "Launch",
  Edit = "Edit",
  Remove = "Remove",
  Terminate = "Terminate",
  Open = "Open",
  Stop = "Pause",
  Resume = "Resume",
  Delete = "Delete",
  EditTimeout = "Edit Timeout",
  RevealPassword = "Reveal Password",
}

export const PROJECT_DETAIL_CONTEXT_MENU_OPTIONS = [
  { name: "Delete Project", href: "#", id: "delete-project" },
];

export enum USERS_TABLE_ACTIONS {
  EDIT_USER = "Edit User",
  ENABLE_PROJECT_BUCKET = "Enable Project Bucket",
  DISABLE_PROJECT_BUCKET = "Disable Project Bucket",
  ENABLE_COST_PREDICTION = "Enable Cost Prediction",
  DISABLE_COST_PREDICTION = "Disable Cost Prediction",
  ACTIVATE_USER = "Activate User",
  DEACTIVATE_USER = "Deactivate User",
  DELETE_USER = "Delete User",
  CHANGE_PASSWORD = "Change Password",
  RESET_MFA = "Reset MFA",
}

export enum USER_ORGANIZATIONS_TABLE_ACTIONS {
  VIEW_USERS = "View Users",
  ADD_USERS = "Add Users",
  EDIT = "Edit Details",
}

export const USER_ORGANIZATIONS_TABLE = [
  { name: USER_ORGANIZATIONS_TABLE_ACTIONS.VIEW_USERS, href: "#" },
  { name: USER_ORGANIZATIONS_TABLE_ACTIONS.ADD_USERS, href: "#" },
  { name: USER_ORGANIZATIONS_TABLE_ACTIONS.EDIT, href: "#" },
];

export enum WORKFLOW_STATUS {
  RELEASED = "released",
  DEVELOPMENT = "development",
  ARCHIVED = "archived",
}

export const WORKFLOW_STATUS_OPTIONS = [
  {
    label: "Released",
    value: WORKFLOW_STATUS.RELEASED,
  },
  {
    label: "Development",
    value: WORKFLOW_STATUS.DEVELOPMENT,
  },
  {
    label: "Archived",
    value: WORKFLOW_STATUS.ARCHIVED,
  },
];

export const USER_ROLE_OPTIONS = [
  {
    label: "User",
    value: UserRole.User,
  },
  {
    label: "Admin",
    value: UserRole.Admin,
  },
  { label: "Guest", value: UserRole.Guest },
];

export const USER_LOGIN_TYPE_OPTIONS = [
  {
    label: "Password",
    value: LoginType.PASSWORD,
  },
  {
    label: "Other",
    value: LoginType.OTHER,
  },
];

export const PREMIUM_DATASET_TABLE = [
  { name: "Open Dataset", href: "#" },
  { name: "Dataset Settings", href: "#" },
  { name: "Copy to Cloud", href: "#" },
  { name: "Delete Dataset", href: "#" },
];

export const PREMIUM_DATASET_PERMISSION_OPTIONS = [
  { value: "user", label: "User" },
  { value: "group", label: "Group" },
];

export const PREMIUM_DATASET_FILE_TABLE = [
  { name: "Edit", href: "#" },
  { name: "Delete", href: "#" },
];

export const ORGANIZATION_TABS = [
  { name: "Activity", href: "activity" },
  { name: "Usage", href: "usage" },
  { name: "Invoices", href: "invoices" },
  { name: "Run Environments", href: "run-environments" },
  { name: "Users", href: "users" },
  { name: "Settings", href: "settings" },
];

export const BLOCK_ORGANIZATION_STEPS = [
  OnboardingStep.AccountCreated,
  OnboardingStep.AgreementsAccepted,
  OnboardingStep.PaymentCompleted,
];

export enum ORGANIZATIONS_INVOICES_TABLE_ACTIONS {
  DOWNLOAD = "Download PDF",
  VIEW_ON_EXTERNAL = "View on Stripe",
}

export const ORGANIZATIONS_INVOICES_TABLE = [
  { name: ORGANIZATIONS_INVOICES_TABLE_ACTIONS.DOWNLOAD, href: "#" },
  { name: ORGANIZATIONS_INVOICES_TABLE_ACTIONS.VIEW_ON_EXTERNAL, href: "#" },
];

// One door. Each of the six entries this replaced opened its own dialog with its own idea of what
// an organization is, and boundary attachment was not among them at all.
export const ORGANIZATION_V2_TABLE = [{ name: "Manage", href: "#" }];

export const METADATA_CONFIGURATION_TABLE = [
  { name: "Edit Collection", href: "#" },
  { name: "Delete Collection", href: "#" },
];

export const METADATA_FIELDS_TABLE = [
  { name: "Edit Field", href: "#" },
  { name: "Delete Field", href: "#" },
];

export const SAMPLE_TRACKERS_TABLE_OPTIONS = [
  { name: "View", href: "#" },
  { name: "Edit", href: "#" },
  { name: "Delete", href: "#" },
];

export const METADATA_CONFIGURATION_TABS = {
  ALL_COLLECTIONS: "All Collections",
  FIELDS: "Fields",
  API_CONFIG: "API Config",
  DATA_IMPORT: "Data Import",
  EVENT_FORMS: "Event Forms",
};

export const METADATA_DATAVIEW_TABS = {
  COLLECTIONS: "Collections",
  SAMPLE_EXPLORER: "Sample Explorer",
  EVENTS: "Events",
};

export enum PATCodeType {
  Mcp = "MCP",
  PythonSdk = "Python SDK",
  RSdk = "R SDK",
  Cli = "CLI",
}

export const PAT_SDK_TABS: { name: PATCodeType }[] = [
  { name: PATCodeType.PythonSdk },
  { name: PATCodeType.RSdk },
  { name: PATCodeType.Cli },
];

export const PAT_MCP_TABS: { name: PATCodeType }[] = [
  { name: PATCodeType.Mcp },
  ...PAT_SDK_TABS,
];

export const PAT_MCP_IDE_OPTIONS: { value: McpIdeType; label: string }[] = [
  { value: "cursor", label: "Cursor" },
  { value: "vscode", label: "VS Code" },
  { value: "copilot", label: "Copilot CLI" },
  { value: "codex", label: "Codex CLI" },
];

export const DEFAULT_EXCLUDED_VMETA_COLUMNS_SAMPLE_EXPLORER = [
  "file_layout",
  "pairing_type",
  "id",
  "archive_cred_id",
  "service_token_id",
  "provider",
  "provider_ids",
  "geo_id",
  "canvas_label",
  "amazon_cre_id",
  "google_cre_id",
];

export const HIDDEN_BY_DEFAULT_COLUMNS_SAMPLE_EXPLORER = new Set([
  "file1",
  "file2",
  "file3",
  "file4",
  "id",
  "canvas_label",
  "archive_dir",
  "s3_archive_dir",
  "gs_archive_dir",
  "provider_ids",
  "file_layout",
]);

/** The agent docs section on upgrading (agent-5-5). The docs site answers at this path. */
export const AGENT_UPGRADE_DOCS_PATH =
  "/user-guides/profile-guide/agent-data-folders/#upgrading";

/** The fcdocs page on what an agent run environment does differently (agent-4-2). The docs site answers at this path. */
export const AGENT_LIMITATIONS_DOCS_PATH =
  "/user-guides/profile-guide/agent-limitations/";
